import { useState } from 'react';
import { Button } from '../../../components/ui/Button.js';
import { FormField, Input, Select } from '../../../components/ui/FormField.js';
import { Modal } from '../../../components/ui/Modal.js';
import {
  CONDITION_TYPES,
  type ConditionType,
  type PricingRule,
  RATE_UNITS,
  type RateUnit,
  type UpdateType,
  type ValueKind,
  WEEKDAY_LABELS,
} from '../types.js';

interface Props {
  open: boolean;
  initial: PricingRule;
  currencySymbol: string;
  onCancel: () => void;
  onSave: (rule: PricingRule) => void;
}

const HALF_HOUR_TIMES = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of ['00', '30']) out.push(`${String(h).padStart(2, '0')}:${m}`);
  }
  return out;
})();

export function PricingRuleDrawer({ open, initial, currencySymbol, onCancel, onSave }: Props) {
  const [rule, setRule] = useState<PricingRule>(initial);
  const [showWho, setShowWho] = useState(
    initial.voucher_ids.length > 0 || initial.allowed_emails.length > 0,
  );
  const [error, setError] = useState<string | null>(null);

  function patch(p: Partial<PricingRule>) {
    setRule((r) => ({ ...r, ...p }));
  }
  function patchParams(p: Record<string, unknown>) {
    setRule((r) => ({ ...r, condition_params: { ...r.condition_params, ...p } }));
  }

  function changeCondition(type: ConditionType) {
    // reset params to the shape for the new type
    const base: Record<string, unknown> = { type };
    if (type === 'time_of_day')
      Object.assign(base, { start_time: '08:00', end_time: '12:00', days_of_week: null });
    if (type === 'date_range') Object.assign(base, { start: '', end: '', repeat_annual: false });
    if (type === 'start_weekday' || type === 'end_weekday') Object.assign(base, { days: [] });
    if (type === 'duration_days' || type === 'advance_booking' || type === 'days_from_start')
      Object.assign(base, { min_days: null, max_days: null });
    if (type === 'duration_minutes') Object.assign(base, { min_minutes: null, max_minutes: null });
    if (type === 'voucher') Object.assign(base, { voucher_ids: [] });
    if (type === 'allowed_emails') Object.assign(base, { emails: [] });
    if (type === 'user_role') Object.assign(base, { roles: [] });
    if (type === 'logged_in') Object.assign(base, { value: true });
    setRule((r) => ({ ...r, condition_type: type, condition_params: { type, ...base } }));
  }

  function toggleWeekday(day: number) {
    const cur = (rule.condition_params.days as number[]) ?? [];
    const next = cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day].sort();
    patchParams({ days: next });
  }

  function toggleTodWeekday(day: number) {
    const cur = (rule.condition_params.days_of_week as number[] | null) ?? [];
    const next = cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day].sort();
    patchParams({ days_of_week: next.length ? next : null });
  }

  function changeValueKind(kind: ValueKind) {
    patch({ value_kind: kind, unit: kind === 'percentage' ? null : (rule.unit ?? 'per_day') });
  }

  function validate(): string | null {
    const p = rule.condition_params;
    if (rule.condition_type === 'time_of_day' && (!p.start_time || !p.end_time))
      return 'Start and end time are required';
    if (rule.condition_type === 'date_range' && (!p.start || !p.end))
      return 'Start and end date are required';
    if (rule.value_kind === 'amount' && !rule.unit) return 'Unit is required for amount values';
    if (rule.value_kind === 'percentage' && (rule.value < 0 || rule.value > 100))
      return 'Percentage must be 0–100';
    if (rule.value_kind === 'amount' && (!Number.isFinite(rule.value) || rule.value < 0))
      return 'Amount must be a non-negative number';
    return null;
  }

  function handleSave() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    // amount values are entered as currency; convert to minor units on save
    onSave(rule);
  }

  const params = rule.condition_params;
  const displayValue =
    rule.value_kind === 'amount'
      ? Number.isFinite(rule.value)
        ? rule.value / 100
        : 0
      : Number.isFinite(rule.value)
        ? rule.value
        : 0;

  return (
    <Modal open={open} onClose={onCancel} title="Pricing rule" size="xl">
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Name" name="rule_name">
            <Input value={rule.name ?? ''} onChange={(e) => patch({ name: e.target.value })} />
          </FormField>
          <FormField label="Enabled" name="rule_enabled">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={rule.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              Active
            </label>
          </FormField>
        </div>

        {/* ── WHEN ── */}
        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-steel">When</h3>
          <FormField label="Condition" name="condition_type">
            <Select
              value={rule.condition_type}
              onChange={(e) => changeCondition(e.target.value as ConditionType)}
            >
              {CONDITION_TYPES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </FormField>

          {rule.condition_type === 'time_of_day' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <FormField label="Start" name="start_time">
                  <Select
                    value={(params.start_time as string) ?? '08:00'}
                    onChange={(e) => patchParams({ start_time: e.target.value })}
                  >
                    {HALF_HOUR_TIMES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="End" name="end_time">
                  <Select
                    value={(params.end_time as string) ?? '12:00'}
                    onChange={(e) => patchParams({ end_time: e.target.value })}
                  >
                    {HALF_HOUR_TIMES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </FormField>
              </div>
              <WeekdayPicker
                selected={(params.days_of_week as number[] | null) ?? []}
                onToggle={toggleTodWeekday}
                label="Days of week (optional)"
              />
            </div>
          )}

          {rule.condition_type === 'date_range' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormField label="From" name="dr_start">
                <Input
                  type="date"
                  value={(params.start as string) ?? ''}
                  onChange={(e) => patchParams({ start: e.target.value })}
                />
              </FormField>
              <FormField label="To" name="dr_end">
                <Input
                  type="date"
                  value={(params.end as string) ?? ''}
                  onChange={(e) => patchParams({ end: e.target.value })}
                />
              </FormField>
            </div>
          )}

          {(rule.condition_type === 'start_weekday' || rule.condition_type === 'end_weekday') && (
            <WeekdayPicker
              selected={(params.days as number[]) ?? []}
              onToggle={toggleWeekday}
              label="Weekdays"
            />
          )}

          {(rule.condition_type === 'duration_days' ||
            rule.condition_type === 'advance_booking' ||
            rule.condition_type === 'days_from_start') && (
            <RangeFields
              minKey="min_days"
              maxKey="max_days"
              unit="days"
              params={params}
              onChange={patchParams}
            />
          )}

          {rule.condition_type === 'duration_minutes' && (
            <RangeFields
              minKey="min_minutes"
              maxKey="max_minutes"
              unit="minutes (×30)"
              step={30}
              params={params}
              onChange={patchParams}
            />
          )}

          {rule.condition_type === 'logged_in' && (
            <FormField label="Requires login" name="logged_in">
              <Select
                value={params.value ? 'yes' : 'no'}
                onChange={(e) => patchParams({ value: e.target.value === 'yes' })}
              >
                <option value="yes">Logged in</option>
                <option value="no">Guest</option>
              </Select>
            </FormField>
          )}
        </section>

        {/* ── HOW ── */}
        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-steel">How</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField label="Action" name="update_type">
              <Select
                value={rule.update_type}
                onChange={(e) => patch({ update_type: e.target.value as UpdateType })}
              >
                <option value="override">Override (set)</option>
                <option value="increase">Increase</option>
                <option value="discount">Discount</option>
              </Select>
            </FormField>
            <FormField label="Value type" name="value_kind">
              <Select
                value={rule.value_kind}
                onChange={(e) => changeValueKind(e.target.value as ValueKind)}
              >
                <option value="amount">Amount ({currencySymbol})</option>
                <option value="percentage">Percentage (%)</option>
              </Select>
            </FormField>
            <FormField
              label={rule.value_kind === 'percentage' ? 'Value (%)' : `Value (${currencySymbol})`}
              name="value"
            >
              <Input
                type="number"
                step={rule.value_kind === 'percentage' ? '1' : '0.01'}
                min="0"
                value={displayValue}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  // amount values are stored in minor units; percentage stored as-is
                  patch({ value: rule.value_kind === 'amount' ? Math.round(n * 100) : n });
                }}
              />
            </FormField>
          </div>
          {rule.value_kind === 'amount' && (
            <FormField label="Unit" name="unit" hint="Multiplier basis (per_day × days, etc.)">
              <Select
                value={rule.unit ?? 'per_day'}
                onChange={(e) => patch({ unit: e.target.value as RateUnit })}
              >
                {RATE_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Select>
            </FormField>
          )}
        </section>

        {/* ── WHO (optional) ── */}
        <section className="space-y-3">
          <button
            type="button"
            className="text-xs font-semibold uppercase tracking-wide text-steel hover:text-ink"
            onClick={() => setShowWho((s) => !s)}
          >
            {showWho ? '▾' : '▸'} Who (optional gates)
          </button>
          {showWho && (
            <div className="space-y-3">
              <FormField
                label="Voucher IDs"
                name="voucher_ids"
                hint="Comma-separated; stored only (enforced later)"
              >
                <Input
                  value={rule.voucher_ids.join(', ')}
                  onChange={(e) =>
                    patch({
                      voucher_ids: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </FormField>
              <FormField label="Allowed emails" name="allowed_emails" hint="Comma-separated">
                <Input
                  value={rule.allowed_emails.join(', ')}
                  onChange={(e) =>
                    patch({
                      allowed_emails: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </FormField>
            </div>
          )}
        </section>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 border-t border-hairline pt-4">
          <Button type="button" onClick={handleSave}>
            Save rule
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function WeekdayPicker({
  selected,
  onToggle,
  label,
}: {
  selected: number[];
  onToggle: (day: number) => void;
  label: string;
}) {
  return (
    <FormField label={label} name="weekdays">
      <div className="flex flex-wrap gap-2">
        {WEEKDAY_LABELS.map((lbl, day) => (
          <label key={lbl} className="flex items-center gap-1 text-sm text-ink">
            <input
              type="checkbox"
              checked={selected.includes(day)}
              onChange={() => onToggle(day)}
            />
            {lbl}
          </label>
        ))}
      </div>
    </FormField>
  );
}

function RangeFields({
  minKey,
  maxKey,
  unit,
  step,
  params,
  onChange,
}: {
  minKey: string;
  maxKey: string;
  unit: string;
  step?: number;
  params: Record<string, unknown>;
  onChange: (p: Record<string, unknown>) => void;
}) {
  const num = (v: string) => (v === '' ? null : Number(v));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <FormField label={`Min ${unit}`} name={minKey}>
        <Input
          type="number"
          min="0"
          step={step ?? 1}
          value={(params[minKey] as number | null) ?? ''}
          onChange={(e) => onChange({ [minKey]: num(e.target.value) })}
        />
      </FormField>
      <FormField label={`Max ${unit}`} name={maxKey}>
        <Input
          type="number"
          min="0"
          step={step ?? 1}
          value={(params[maxKey] as number | null) ?? ''}
          onChange={(e) => onChange({ [maxKey]: num(e.target.value) })}
        />
      </FormField>
    </div>
  );
}
